import { useEffect, useState, useSyncExternalStore } from 'react';
import { useRunStore } from '../store/runStore';
import { unlockAudio } from '../game/audio';
import { useT, type TKey } from '../i18n';
import { getLastServerRunId, subscribeLastServerRunId, getLastFishGained, subscribeLastFishGained } from '../api';
import { ShareButton } from './ShareButton';
import { ConfettiBurst } from './decor/ConfettiBurst';

/** Animate a numeric value from 0 to `target` over `durationMs`. */
function useCountUp(target: number, durationMs = 1200): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target === 0) {
      setValue(0);
      return;
    }
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(eased * target));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, durationMs]);
  return value;
}

export function EndScreen() {
  const phase = useRunStore((s) => s.phase);
  const kills = useRunStore((s) => s.enemiesKilled);
  const loot = useRunStore((s) => s.lootCount);
  const owned = useRunStore((s) => s.ownedPerks);
  const reset = useRunStore((s) => s.resetToMenu);
  const startRun = useRunStore((s) => s.startRun);
  const { t } = useT();

  const won = phase === 'won';
  const killsAnim = useCountUp(kills);
  const lootAnim = useCountUp(loot);
  const runId = useSyncExternalStore(subscribeLastServerRunId, getLastServerRunId, getLastServerRunId);
  const fishGained = useSyncExternalStore(subscribeLastFishGained, getLastFishGained, getLastFishGained);

  const handleAgain = () => {
    unlockAudio();
    startRun();
  };

  return (
    <div
      className="scanlines overlay-enter"
      role="dialog"
      aria-modal="true"
      aria-labelledby="endscreen-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg-overlay-strong)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-4)',
        padding: 'var(--space-6)',
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + var(--space-6))',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + var(--space-6))',
        zIndex: 20,
        overflow: 'auto',
        touchAction: 'pan-y',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {won && <ConfettiBurst key={runId ?? 'won'} />}
      <div className="scan-band" />

      <div className="label" style={{ color: 'var(--fg-muted)' }}>
        {won ? t('end.label.won') : t('end.label.lost')}
      </div>

      <h2
        id="endscreen-title"
        className={`headline slide-in-up ${won ? 'glow-success pulse' : 'glow-danger blink'}`}
        style={{ textAlign: 'center' }}
      >
        {won ? t('end.title.won') : t('end.title.lost')}
      </h2>

      <p style={{
        opacity: 0.75,
        textAlign: 'center',
        maxWidth: '20rem',
        fontSize: '0.85rem',
        lineHeight: 1.4,
      }}>
        {won ? t('end.lore.won') : t('end.lore.lost')}
      </p>

      <div
        className="terminal-frame slide-in-up"
        style={{
          minWidth: '17rem',
          maxWidth: '22rem',
          width: '100%',
          fontSize: '0.78rem',
        }}
      >
        <span className="corner-tr" />
        <span className="corner-bl" />
        <div className="label" style={{ color: 'var(--fg-muted)' }}>
          {t('end.debrief.title')}
        </div>
        <hr className="divider" />
        <dl style={{ display: 'grid', gap: 'var(--space-2)', margin: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <dt className="label">{t('end.debrief.kills')}</dt>
            <dd className="glow-primary" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {killsAnim.toString().padStart(3, '0')}
            </dd>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <dt className="label">{t('end.debrief.loot')}</dt>
            <dd className="glow-warning" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {lootAnim.toString().padStart(3, '0')}
            </dd>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <dt className="label">{t('end.debrief.status')}</dt>
            <dd
              className={won ? 'glow-success' : 'glow-danger'}
              style={{ fontWeight: 'bold' }}
            >
              {won ? t('end.debrief.extracted') : t('end.debrief.kia')}
            </dd>
          </div>
        </dl>

        {fishGained > 0 && (
          <div style={{ color: 'var(--fg-secondary)', fontSize: '0.9rem', marginTop: 'var(--space-2)' }}>
            {t('endScreen.fishEarned').replace('{n}', String(fishGained))}
          </div>
        )}

        <hr className="divider" />

        <div className="label" style={{ color: 'var(--fg-muted)', marginBottom: 'var(--space-2)' }}>
          {t('end.debrief.installed')}
        </div>
        {owned.length === 0 ? (
          <div style={{ opacity: 0.5, fontSize: '0.75rem' }}>{t('end.debrief.none')}</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {owned.map((id, i) => {
              const localizedName = t(`perk.${id}.name` as TKey);
              const letter = localizedName.charAt(0);
              return (
                <span
                  key={`${id}-${i}`}
                  title={localizedName}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    border: '1px solid var(--fg-primary)',
                    borderRadius: 2,
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    color: 'var(--fg-primary)',
                    background: 'rgba(0, 255, 255, 0.08)',
                    boxShadow: '0 0 6px rgba(0, 255, 255, 0.3)',
                  }}
                >
                  {letter}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', justifyContent: 'center' }}>
        <button className="primary" onClick={reset}>
          {t('end.btn.return')}
        </button>
        <button className={won ? '' : 'danger'} onClick={handleAgain}>
          {t('end.btn.again')}
        </button>
        <ShareButton runId={runId} />
      </div>
    </div>
  );
}
